import React, { useEffect, useRef, useState } from 'react';

const DISMISS_KEY = 'tracern:installBannerDismissed';

function isStandalone() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari standalone
  if (window.navigator.standalone) return true;
  return false;
}

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const narrow = window.innerWidth < 900;
  const ipadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return mobileUa || ipadOs || narrow;
}

function isIos() {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Prompts mobile users to install Tracern (PWA) on first login.
 * Dismiss is remembered in localStorage.
 */
export default function InstallAppBanner() {
  const [visible, setVisible] = useState(false);
  const [iosSteps, setIosSteps] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const deferredPrompt = useRef(null);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (isStandalone()) return;
    if (!isMobileDevice()) return;

    setVisible(true);

    const onInstallable = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', onInstallable);
    return () => window.removeEventListener('beforeinstallprompt', onInstallable);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      try {
        await deferredPrompt.current.prompt();
        const { outcome } = await deferredPrompt.current.userChoice;
        deferredPrompt.current = null;
        setCanInstall(false);
        if (outcome === 'accepted') dismiss();
      } catch {
        /* prompt may fail if already used */
      }
      return;
    }

    if (isIos()) {
      setIosSteps(true);
      return;
    }

    // Android / other — menu path when prompt not available yet
    setIosSteps(true);
  };

  if (!visible) return null;

  const onIos = isIos();

  return (
    <div style={{
      padding: '12px 16px',
      background: 'linear-gradient(90deg, #EFF6FF 0%, #F0FDF4 100%)',
      borderBottom: '1px solid #BFDBFE',
      position: 'sticky',
      top: 0,
      zIndex: 59,
      fontSize: '13px',
      color: '#1E3A5F',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '12px',
        maxWidth: '800px',
        margin: '0 auto',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span aria-hidden>📲</span>
            Install Tracern on your phone
          </p>
          <p style={{ lineHeight: 1.5, color: '#374151' }}>
            Add a home-screen icon — open Tracern like an app without typing the URL each time.
          </p>

          {iosSteps && (
            <ol style={{
              marginTop: '10px',
              paddingLeft: '18px',
              lineHeight: 1.6,
              color: '#4B5563',
              fontSize: '12px',
            }}>
              {onIos ? (
                <>
                  <li>Tap the <strong>Share</strong> button in Safari (square with arrow)</li>
                  <li>Scroll and tap <strong>Add to Home Screen</strong></li>
                  <li>Tap <strong>Add</strong> — the Tracern icon appears on your home screen</li>
                </>
              ) : (
                <>
                  <li>Open the browser menu (⋮)</li>
                  <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
                  <li>Confirm — the Tracern icon appears on your home screen</li>
                </>
              )}
            </ol>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleInstall}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#2563EB',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {canInstall ? 'Install now' : onIos ? 'Show me how' : 'How to install'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            style={{
              padding: '4px 8px',
              border: 'none',
              background: 'none',
              color: '#6B7280',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#9CA3AF',
            lineHeight: 1,
            padding: 0,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
